import {
  loadQueue,
  removeQueuedExpense,
  updateQueuedExpense,
  tryAcquireFlushLock,
  releaseFlushLock,
  type ExpenseFormInputLike,
} from "./offline-expense-queue";

export type FlushResult = { flushed: number; remaining: number };

/**
 * Whether a thrown value looks like a network-level failure rather than an
 * application/validation error. See docs/superpowers/plans/2026-08-26-offline-expense-tasks.md
 * (Task 1 report) for the empirical basis: this app's Server Actions always throw a
 * plain `Error` (`throw new Error("...")`) for validation failures, which the client
 * receives as-is; a genuine network failure (server unreachable, offline) instead
 * fails inside the browser's own fetch() call underlying the Server Action request,
 * which the Fetch spec defines as rejecting with a `TypeError` ("Failed to fetch" in
 * Chromium) — a distinct, standard class never used for our own thrown errors.
 */
export function isNetworkError(err: unknown): boolean {
  // SPA(Worker 판)에서는 fetch를 우리가 감싸고 있어서 TypeError가 그대로 올라오지
  // 않는다 — web/src/api/client.ts가 `offline` 꼬리표를 붙인 ApiError로 바꿔 던진다.
  // 두 세계를 같은 함수가 판별해야 해서 둘 다 본다.
  if (err instanceof TypeError) return true;
  return typeof err === "object" && err !== null && (err as { offline?: unknown }).offline === true;
}

/**
 * Sequentially replays a trip's queued expenses through `createFn` (createExpenseAction
 * in production — injected so this stays testable without a real network/server
 * action). Guarded by tryAcquireFlushLock so overlapping triggers (the `online` event,
 * the manual "지금 동기화" button, and the on-mount effect can all fire close together)
 * never double-submit.
 *
 * A validation failure leaves that item queued with its error message (see
 * ExpenseFormDialog's queued-item edit/retry). A network-looking failure — or
 * navigator.onLine going false mid-run — stops the loop instead: every remaining item
 * (including the one that just failed) stays queued untouched, for the next automatic
 * or manual flush, rather than being wrongly tagged as a validation error.
 */
export async function flushQueue(
  tripId: string,
  createFn: (tripId: string, input: ExpenseFormInputLike & { clientId: string }) => Promise<void>
): Promise<FlushResult> {
  if (!tryAcquireFlushLock(tripId)) {
    return { flushed: 0, remaining: loadQueue(tripId).length };
  }

  try {
    let flushed = 0;
    for (const item of loadQueue(tripId)) {
      if (typeof navigator !== "undefined" && !navigator.onLine) break;
      try {
        await createFn(tripId, { ...item.input, clientId: item.clientId });
        removeQueuedExpense(tripId, item.clientId);
        flushed++;
      } catch (err) {
        if (isNetworkError(err)) break;
        updateQueuedExpense(tripId, item.clientId, {
          error: err instanceof Error ? err.message : "저장에 실패했습니다",
        });
      }
    }
    return { flushed, remaining: loadQueue(tripId).length };
  } finally {
    releaseFlushLock(tripId);
  }
}
